import { useState } from 'react';
import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { Alert, Button, LinkButton } from 'components/common';
import { RegisterForm } from 'components/register';
import {
  AuthStrategyFormLogin,
  GuestLogin,
  LDAPLogin,
  LocalLogin,
  SingleSignOn
} from 'components/login';

import type { ServerConfig } from 'shared-types';
import type { RegisterFormProps } from 'components/register/RegisterForm';

interface Props {
  availableAdditionalStrategies: ServerConfig['authStrategies'];
  initialActiveStrategy: string | null;
  handleActiveStrategyChange: (type: string | null) => void;
  localLoginOpenButtonText?: string;
  next: string | null;
  onLocalLoginSuccess?: () => void;
  registerFormProps?: RegisterFormProps;
  registrationLinkEnabled: boolean;
  useEmbeddedRegister?: boolean;
  useGuestCourseLogin?: boolean;
  useGuestHomeLogin?: boolean;
  useLocalLogin: boolean;
}

const LoginContainer = ({
  availableAdditionalStrategies,
  initialActiveStrategy,
  handleActiveStrategyChange,
  localLoginOpenButtonText,
  next,
  onLocalLoginSuccess,
  registerFormProps,
  registrationLinkEnabled,
  useEmbeddedRegister,
  useGuestCourseLogin,
  useGuestHomeLogin,
  useLocalLogin
}: Props) => {
  const { language, messages, serverConfig, theme } = useReduxState();

  const [authType, setAuthType] = useState<'login' | 'two-factor' | 'register'>(
    'login'
  );

  const [activeStrategy, setActiveStrategy] = useState(initialActiveStrategy);

  const router = useRouter();

  const handleGoBackClick = () => {
    setAuthType('login');
    setActiveStrategy(initialActiveStrategy);
    handleActiveStrategyChange(initialActiveStrategy);
  };

  const handleLocalLoginLinkClick = () => {
    handleActiveStrategyChange('local');
    setActiveStrategy('local');
  };

  const handleRegistrationLinkClick = () => {
    setAuthType('register');
  };

  const renderRegisterButton = () =>
    useEmbeddedRegister ? (
      <Button
        category="light"
        onClick={handleRegistrationLinkClick}
        style={{ width: '100%' }}
        text={messages.authRegister}
      />
    ) : (
      <LinkButton
        category="light"
        customBackgroundColor={
          serverConfig.loginPageRegisterButton?.backgroundColor
        }
        customBackgroundColorHover={
          serverConfig.loginPageRegisterButton?.backgroundColorHover
        }
        customColor={serverConfig.loginPageRegisterButton?.color}
        href="/register"
        style={{ width: '100%' }}
        text={
          serverConfig.loginPageRegisterButton?.textLocales?.[language] ||
          serverConfig.loginPageRegisterButton?.text ||
          messages.authRegister
        }
      />
    );

  const additionalStrategies = activeStrategy
    ? availableAdditionalStrategies.filter(
        (strategy) => strategy.name === activeStrategy
      )
    : availableAdditionalStrategies;

  const showGuestLogin =
    (useGuestCourseLogin || useGuestHomeLogin) && !activeStrategy;

  return (
    <div className="login-container">
      <div className="content">
        {authType === 'register' ||
        (activeStrategy &&
          (useGuestCourseLogin ||
            useGuestHomeLogin ||
            availableAdditionalStrategies.length > 0 ||
            (registrationLinkEnabled &&
              serverConfig.loginPageRegisterLinkAsButton))) ? (
          <div style={{ marginBottom: 15 }}>
            <Button
              category="link"
              icon="arrow-left"
              iconStyle={{ fontSize: '1rem' }}
              isRounded={true}
              onClick={handleGoBackClick}
              size="medium"
              style={{
                color: theme.textColorLight,
                fontSize: '1.4rem',
                fontWeight: 600
              }}
              text={messages.commonGoBack}
            />
          </div>
        ) : null}

        {useEmbeddedRegister && authType === 'register' ? (
          <RegisterForm next={next} {...registerFormProps} />
        ) : (
          <>
            {registrationLinkEnabled &&
            !activeStrategy &&
            serverConfig.loginPageRegisterButton?.positionAtTop ? (
              <div style={{ marginTop: 15 }}>{renderRegisterButton()}</div>
            ) : null}

            {showGuestLogin &&
            serverConfig.loginPageGuestLoginButton?.positionAtTop ? (
              <GuestLogin
                guestLoginOnly={!useLocalLogin && !additionalStrategies.length}
                isHomePageLogin={useGuestHomeLogin}
                next={next || '/'}
              />
            ) : null}

            {additionalStrategies.length
              ? additionalStrategies.map((strategy) =>
                  strategy.type === 'api-endpoint' ? (
                    <AuthStrategyFormLogin
                      isOpen={activeStrategy === strategy.name}
                      key={strategy.name}
                      next={next}
                      onOpenClick={setActiveStrategy}
                      strategy={strategy}
                    />
                  ) : strategy.type === 'ldap' ? (
                    <LDAPLogin
                      isOpen={activeStrategy === strategy.name}
                      key={strategy.name}
                      next={next}
                      onOpenClick={setActiveStrategy}
                      strategy={strategy}
                    />
                  ) : (
                    <SingleSignOn
                      key={strategy.name}
                      next={next}
                      strategy={strategy}
                    />
                  )
                )
              : null}

            {useLocalLogin &&
            (!activeStrategy || activeStrategy === 'local') ? (
              <div className="local">
                <LocalLogin
                  isOpen={activeStrategy === 'local'}
                  next={next || '/'}
                  onOpenClick={handleLocalLoginLinkClick}
                  onSuccess={onLocalLoginSuccess}
                  openButtonText={localLoginOpenButtonText}
                  registrationLinkEnabled={registrationLinkEnabled}
                  {...(useEmbeddedRegister
                    ? { registrationLinkOnClick: handleRegistrationLinkClick }
                    : {})}
                />
              </div>
            ) : null}

            {registrationLinkEnabled &&
            !activeStrategy &&
            !serverConfig.loginPageRegisterButton?.positionAtTop ? (
              <div style={{ marginTop: 15 }}>{renderRegisterButton()}</div>
            ) : null}

            {showGuestLogin &&
            !serverConfig.loginPageGuestLoginButton?.positionAtTop ? (
              <GuestLogin
                guestLoginOnly={!useLocalLogin && !additionalStrategies.length}
                isHomePageLogin={useGuestHomeLogin}
                next={next || '/'}
              />
            ) : null}

            {router?.query?.error ? (
              <Alert
                text={
                  {
                    access_denied: messages.readerErrorForbidden,
                    email_not_found: messages.authEmailNotFound
                  }[router.query.error as string] || messages.commonError
                }
                type="error-light"
                style={{ marginTop: 25 }}
              />
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default LoginContainer;
